"use client"

import { X } from "lucide-react"

interface VirtualKeyboardProps {
  onKeyPress: (key: string) => void
  onClose: () => void
  currentValue: string
}

const VirtualKeyboard: React.FC<VirtualKeyboardProps> = ({
  onKeyPress,
  onClose
}) => {
  
  // Layout del teclado QWERTY en español
  const keyboardLayout = [
    ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'],
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ñ'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M']
  ]

  // Función para reproducir sonido de tecla
  const playKeySound = (type: 'normal' | 'special' | 'action' = 'normal') => {
    try {
      // Crear contexto de audio
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      
      // Diferentes frecuencias para diferentes tipos de teclas
      const frequencies = {
        normal: 800,    // Tono medio para teclas normales
        special: 600,   // Tono más grave para backspace/espacio
        action: 1000    // Tono más agudo para enter
      }
      
      const frequency = frequencies[type]
      
      // Crear oscilador
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      // Configurar sonido
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime)
      oscillator.type = 'sine'
      
      // Configurar volumen y duración
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1)
      
      // Reproducir
      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.1)
      
    } catch (error) {
      // Fallback silencioso si no se puede reproducir audio
      console.log('Audio no disponible')
    }
  }

  const handleKeyPress = (key: string) => {
    playKeySound('normal')
    onKeyPress(key)
  }

  const handleSpecialKey = (action: string) => {
    if (action === '{enter}') {
      playKeySound('action')
    } else {
      playKeySound('special')
    }
    onKeyPress(action)
  }

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-2xl border border-white/20">
      {/* Botón de cerrar */}
      <div className="flex justify-end mb-2">
        <button
          onClick={onClose}
          className="w-8 h-8 bg-red-500/20 hover:bg-red-500/30 rounded-xl flex items-center justify-center transition-all duration-200"
        >
          <X className="h-4 w-4 text-red-600" />
        </button>
      </div>

      {/* Teclado */}
      <div className="space-y-3">
        {/* Fila de números */}
        <div className="flex justify-center space-x-2">
          {keyboardLayout[0].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className="w-12 h-12 bg-gray-200 hover:bg-gray-300 active:bg-gray-400 rounded-xl text-lg font-medium text-gray-800 transition-all duration-150 touch-manipulation"
            >
              {key}
            </button>
          ))}
        </div>

        {/* Primera fila de letras */}
        <div className="flex justify-center space-x-2">
          {keyboardLayout[1].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className="w-12 h-12 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-lg font-medium text-white transition-all duration-150 touch-manipulation"
            >
              {key}
            </button>
          ))}
        </div>

        {/* Segunda fila de letras */}
        <div className="flex justify-center space-x-2">
          {keyboardLayout[2].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className="w-12 h-12 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-lg font-medium text-white transition-all duration-150 touch-manipulation"
            >
              {key}
            </button>
          ))}
        </div>

        {/* Tercera fila de letras */}
        <div className="flex justify-center space-x-2">
          {keyboardLayout[3].map((key) => (
            <button
              key={key}
              onClick={() => handleKeyPress(key)}
              className="w-12 h-12 bg-blue-500 hover:bg-blue-600 active:bg-blue-700 rounded-xl text-lg font-medium text-white transition-all duration-150 touch-manipulation"
            >
              {key}
            </button>
          ))}
        </div>

        {/* Fila de teclas especiales */}
        <div className="flex justify-center space-x-2">
          {/* Backspace */}
          <button
            onClick={() => handleSpecialKey('{bksp}')}
            className="px-6 h-12 bg-red-500 hover:bg-red-600 active:bg-red-700 rounded-xl text-sm font-medium text-white transition-all duration-150 touch-manipulation"
          >
            ⌫ Borrar
          </button>

          {/* Espacio */}
          <button
            onClick={() => handleSpecialKey('{space}')}
            className="px-16 h-12 bg-gray-400 hover:bg-gray-500 active:bg-gray-600 rounded-xl text-sm font-medium text-white transition-all duration-150 touch-manipulation"
          >
            Espacio
          </button>

          {/* Enter */}
          <button
            onClick={() => handleSpecialKey('{enter}')}
            className="px-6 h-12 bg-green-500 hover:bg-green-600 active:bg-green-700 rounded-xl text-sm font-medium text-white transition-all duration-150 touch-manipulation"
          >
            ✓ Buscar
          </button>
        </div>
      </div>


      {/* Instrucciones mínimas */}
      <div className="mt-2 text-center">
        <p className="text-xs text-gray-500">
          Toca las teclas para escribir
        </p>
      </div>
    </div>
  )
}

export default VirtualKeyboard
